import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Post-service satisfaction survey modal.
 * Props:
 *  - open, onOpenChange
 *  - appointmentId
 *  - onSubmitted (callback)
 */
const SurveyModal = ({ open, onOpenChange, appointmentId, onSubmitted }) => {
  const { API, sessionToken } = useContext(AuthContext);
  const [nps, setNps] = useState(null);
  const [csat, setCsat] = useState(0);
  const [ces, setCes] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);

  const headers = { Authorization: `Bearer ${sessionToken}` };

  useEffect(() => {
    if (open && appointmentId) {
      // Check if survey already exists
      axios.get(`${API}/appointments/${appointmentId}/survey`, { headers })
        .then(res => {
          setExisting(res.data);
          if (res.data) {
            setNps(res.data.nps);
            setCsat(res.data.csat);
            setCes(res.data.ces);
            setComment(res.data.comment || '');
          }
        })
        .catch(() => { /* ignore */ });
    } else if (!open) {
      setNps(null); setCsat(0); setCes(null); setComment(''); setExisting(null);
    }
  }, [open, appointmentId]);

  const submit = async () => {
    if (nps === null || csat === 0 || ces === null) {
      toast.error('Completa todas las preguntas');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/appointments/${appointmentId}/survey`,
        { nps, csat, ces, comment: comment || null },
        { headers }
      );
      toast.success('¡Gracias por tu calificación!');
      if (onSubmitted) onSubmitted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar la encuesta');
    } finally {
      setSubmitting(false);
    }
  };

  const renderNPSScale = () => (
    <div>
      <p className="text-sm font-medium text-slate-700 mb-2">
        ¿Qué tan probable es que recomiendes este taller a un amigo o familiar?
      </p>
      <div className="grid grid-cols-11 gap-1">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            type="button"
            disabled={!!existing}
            onClick={() => setNps(i)}
            className={`py-2 text-sm font-medium rounded transition-colors ${
              nps === i
                ? 'bg-purple-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            } ${existing ? 'cursor-not-allowed opacity-70' : ''}`}
            data-testid={`nps-${i}`}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-500 mt-1">
        <span>Nada probable</span>
        <span>Muy probable</span>
      </div>
    </div>
  );

  const renderCSATStars = () => (
    <div>
      <p className="text-sm font-medium text-slate-700 mb-2">
        ¿Qué tan satisfecho estás con el servicio recibido?
      </p>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            disabled={!!existing}
            onClick={() => setCsat(i)}
            className="text-3xl transition-transform hover:scale-110"
            data-testid={`csat-${i}`}
          >
            <Star
              className={`w-8 h-8 ${
                csat >= i ? 'text-yellow-400 fill-current' : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  const renderCESScale = () => (
    <div>
      <p className="text-sm font-medium text-slate-700 mb-2">
        ¿Qué tan fácil fue resolver tu necesidad en este taller?
      </p>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 7 }, (_, i) => (
          <button
            key={i + 1}
            type="button"
            disabled={!!existing}
            onClick={() => setCes(i + 1)}
            className={`py-2 text-sm font-medium rounded transition-colors ${
              ces === i + 1
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            } ${existing ? 'cursor-not-allowed opacity-70' : ''}`}
            data-testid={`ces-${i + 1}`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-500 mt-1">
        <span>Muy difícil</span>
        <span>Muy fácil</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="survey-modal">
        <DialogHeader>
          <DialogTitle>
            {existing ? 'Tu calificación' : 'Califica tu experiencia'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {existing && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
              Ya completaste esta encuesta. Gracias por tu retroalimentación.
            </div>
          )}

          {renderNPSScale()}
          {renderCSATStars()}
          {renderCESScale()}

          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">
              Comentario adicional (opcional)
            </p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Cuéntanos un poco más sobre tu experiencia…"
              rows={3}
              disabled={!!existing}
              data-testid="survey-comment-input"
            />
          </div>
        </div>

        <DialogFooter>
          {!existing && (
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-survey-btn"
            >
              {submitting ? 'Enviando…' : 'Enviar calificación'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SurveyModal;
